import { ActionIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCopy, IconExclamationCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProjectName } from '@xver/components/Molecules';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { BaremetalInstance, BaremetalStatus } from '../../../../../../@types/instance';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { focusRowMenu, useMoveResource } from '@xver/hooks/useMoveResource';
import { COPY_IP_COOLDOWN_MS, copyToClipboard } from '@xver/utils/clipboard';
import { priceForPeriod, shortCpuLabel } from '@xver/utils/baremetal';
import { formatDate } from '@xver/utils/date';
import { useDeleteBaremetalMutate } from '../../../../../../hooks/CloudComputeHooks/useDeleteBaremetalMutate';
import { useSendBaremetalCommandMutate } from '../../../../../../hooks/CloudComputeHooks/useSendBaremetalCommandMutate';
import BaremetalActionsMenu from './components/BaremetalActionsMenu';
import BaremetalAvatar from './components/BaremetalAvatar';
import DestroyModal from './components/DestroyModal';

interface BaremetalListTableProps {
    baremetals?: BaremetalInstance[];
    isLoading?: boolean;
}

export const STATUS_BADGE: Record<BaremetalStatus, { bg: string; fg: string }> = {
    running: { bg: '#10b98126', fg: 'var(--color-status-success)' },
    installing: { bg: '#f59e0b26', fg: 'var(--color-status-warning)' },
    starting: { bg: '#3b82f626', fg: 'var(--color-status-info)' },
    rebooting: { bg: '#3b82f626', fg: 'var(--color-status-info)' },
    stopping: { bg: '#f59e0b26', fg: 'var(--color-status-warning)' },
    stopped: { bg: '#9ca3af26', fg: 'var(--color-status-neutral)' },
    destroying: { bg: '#ef444426', fg: 'var(--color-status-danger)' },
};

export function BaremetalListTable({ baremetals, isLoading = false }: BaremetalListTableProps) {
    const { t, i18n } = useTranslation('bareMetal');
    const safeRows = baremetals ?? [];
    const navigate = useNavigate();
    const lastCopiedIpRef = useRef<{ ipAddress: string; copiedAt: number } | null>(null);
    const [loadingId, setLoadingId] = useState<number | null>(null);

    const [destroyOpened, { open: openDestroy, close: closeDestroy }] = useDisclosure(false);
    const [destroyTargetId, setDestroyTargetId] = useState<number | null>(null);

    const sendCommandMutation = useSendBaremetalCommandMutate();
    const deleteMutation = useDeleteBaremetalMutate();
    const { canMove, requestMove, moveModal } = useMoveResource('baremetal');

    const isAnyPending = sendCommandMutation.isPending || deleteMutation.isPending;
    useEffect(() => {
        if (!isAnyPending && loadingId !== null) setLoadingId(null);
    }, [isAnyPending, loadingId]);

    const handleRowClick = useCallback(
        (b: BaremetalInstance) => {
            navigate(`/bare-metal-compute/${b.id}`);
        },
        [navigate]
    );

    const handleCopyIp = useCallback(
        async (ip: string) => {
            const prev = lastCopiedIpRef.current;
            const now = Date.now();
            if (prev && prev.ipAddress === ip && now - prev.copiedAt < COPY_IP_COOLDOWN_MS) return;
            try {
                await copyToClipboard(ip);
                lastCopiedIpRef.current = { ipAddress: ip, copiedAt: now };
                notifications.show({
                    title: t('notifications:ipAddress.copied'),
                    message: t('notifications:ipAddress.copiedMessage', { ipAddress: ip }),
                    icon: <IconCircleCheck size={32} color="oklch(72.3% 0.219 149.579)" />,
                });
            } catch {
                notifications.show({
                    title: t('notifications:ipAddress.copyFailed'),
                    message: t('notifications:ipAddress.copyFailedMessage', { ipAddress: ip }),
                    icon: <IconExclamationCircle size={32} color="#eb5757" />,
                });
            }
        },
        [t]
    );

    const confirmDestroy = useCallback(() => {
        if (destroyTargetId === null) return;
        setLoadingId(destroyTargetId);
        deleteMutation.mutate(destroyTargetId);
        closeDestroy();
        focusRowMenu('baremetal', destroyTargetId, true);
    }, [destroyTargetId, deleteMutation, closeDestroy]);

    const cancelDestroy = useCallback(() => {
        if (destroyTargetId !== null) focusRowMenu('baremetal', destroyTargetId);
        closeDestroy();
    }, [destroyTargetId, closeDestroy]);

    const destroyTargetName = safeRows.find((b) => b.id === destroyTargetId)?.hostname ?? '';

    const makeRowActions = useCallback(
        (b: BaremetalInstance) => ({
            onReboot: () => {
                setLoadingId(b.id);
                sendCommandMutation.mutate({ id: b.id, command: 'reboot' });
            },
            onPowerOff: () => {
                setLoadingId(b.id);
                sendCommandMutation.mutate({ id: b.id, command: 'shutdown' });
            },
            onPowerOn: () => {
                setLoadingId(b.id);
                sendCommandMutation.mutate({ id: b.id, command: 'start' });
            },
            onMove: () => requestMove({ id: String(b.id), name: b.hostname }),
            onDestroy: () => {
                setDestroyTargetId(b.id);
                openDestroy();
            },
        }),
        [sendCommandMutation, openDestroy, requestMove]
    );

    const columns: DataTableColumn<BaremetalInstance>[] = [
        {
            key: 'hostname',
            label: t('table.hostname'),
            render: (b) => (
                <div className="flex flex-row justify-start items-center gap-3">
                    <BaremetalAvatar hostname={b.hostname} status={b.status} />
                    <div className="h-full flex flex-col justify-center items-start gap-2">
                        <h3 className="mt-0.5 text-base font-bold">
                            <Link
                                to={`/bare-metal-compute/${b.id}`}
                                className="hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {b.hostname}
                            </Link>
                            <span
                                className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded"
                                style={{
                                    backgroundColor: STATUS_BADGE[b.status].bg,
                                    color: STATUS_BADGE[b.status].fg,
                                }}
                            >
                                {t(`status:${b.status}`)}
                            </span>
                        </h3>
                        <div className="flex flex-row justify-start items-center gap-1.5 text-sm flex-nowrap whitespace-nowrap text-fg/68">
                            <span>{shortCpuLabel(b.specs.cpu)}</span>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'project',
            label: t('projects:switcher.label'),
            render: (b) => <ProjectName projectId={b.project_id} />,
        },
        {
            key: 'ip',
            label: t('table.ipAddress'),
            render: (b) => (
                <span className="text-base flex-nowrap whitespace-nowrap">
                    {b.network.ip_address}
                    <ActionIcon
                        variant="transparent"
                        aria-label={t('table.copyIpAddress')}
                        onClick={(e) => {
                            e.stopPropagation();
                            void handleCopyIp(b.network.ip_address);
                        }}
                    >
                        <IconCopy size="16" className="text-fg/68" />
                    </ActionIcon>
                </span>
            ),
        },
        {
            key: 'plan',
            label: t('table.plan'),
            render: (b) => (
                <div className="flex flex-col items-start flex-nowrap whitespace-nowrap">
                    <span className="text-sm text-fg">
                        {t(`bareMetalDeployment:paymentPeriod.${b.payment_period}`)}
                    </span>
                    <span className="text-xs text-fg/68">
                        ฿{priceForPeriod(b).toFixed(2)} /{' '}
                        {t(`bareMetalDeployment:paymentPeriod.${b.payment_period}`)}
                    </span>
                </div>
            ),
        },
        {
            key: 'next_charge',
            label: t('table.nextCharge'),
            render: (b) => (
                <div className="flex flex-col items-start flex-nowrap whitespace-nowrap">
                    <span className="text-sm text-fg">
                        {formatDate(b.next_charge, i18n.language, t)}
                    </span>
                    <span className="text-xs text-fg/68">
                        {b.auto_renew ? t('table.autoRenew') : t('table.manual')}
                    </span>
                </div>
            ),
        },
    ];

    return (
        <>
            <DataTable
                className="baremetal-table"
                rows={safeRows}
                isLoading={isLoading}
                columns={columns}
                onRowClick={handleRowClick}
                actions={(b) => (
                    <BaremetalActionsMenu
                        baremetal={b}
                        isLoading={loadingId === b.id}
                        canManage={b.access !== 'view'}
                        canMove={canMove}
                        {...makeRowActions(b)}
                    />
                )}
                rowKey={(b) => b.id}
                skeletonRows={4}
                emptyMessage={t('table.noInstances')}
                caption={t('page.title')}
                actionsLabel={t('table.actions')}
            />

            {moveModal}

            <DestroyModal
                opened={destroyOpened}
                name={destroyTargetName}
                onClose={cancelDestroy}
                onConfirm={confirmDestroy}
            />
        </>
    );
}
